// Serviço de notificações PWA otimizado para iPhone e dispositivos mobile
export class PWANotificationService {
  private static instance: PWANotificationService;
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  private constructor() {}

  public static getInstance(): PWANotificationService {
    if (!PWANotificationService.instance) {
      PWANotificationService.instance = new PWANotificationService();
    }
    return PWANotificationService.instance;
  }

  // Inicializar serviço de notificações
  public async initialize(): Promise<boolean> {
    try {
      // Verificar se service workers são suportados
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers não são suportados neste navegador');
        return false;
      }

      // Verificar se notificações são suportadas
      if (!('Notification' in window)) {
        console.warn('Notificações não são suportadas neste navegador');
        return false;
      }

      // Registrar service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('✅ Service Worker registrado com sucesso', this.registration);

      // Aguardar service worker estar ativo
      await this.waitForServiceWorker();

      return true;
    } catch (error) {
      console.error('❌ Erro ao inicializar PWA:', error);
      return false;
    }
  }

  // Aguardar service worker estar ativo
  private async waitForServiceWorker(): Promise<void> {
    return new Promise((resolve) => {
      if (this.registration?.active) {
        resolve();
        return;
      }

      const worker = this.registration?.installing || this.registration?.waiting;
      if (worker) {
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  }

  // Solicitar permissão para notificações (otimizado para iPhone)
  public async requestNotificationPermission(): Promise<NotificationPermission> {
    try {
      // No iPhone Safari, deve ser chamado por interação do usuário
      let permission = Notification.permission;

      if (permission === 'default') {
        // Para iPhone, mostra prompt nativo
        permission = await Notification.requestPermission();
      }

      console.log('📱 Permissão de notificação:', permission);
      return permission;
    } catch (error) {
      console.error('❌ Erro ao solicitar permissão:', error);
      return 'denied';
    }
  }

  // Verificar se o app pode ser instalado
  public async canInstallPWA(): Promise<boolean> {
    // Verificar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return false; // Já está instalado
    }

    // Verificar se há evento beforeinstallprompt (Android)
    return 'beforeinstallprompt' in window;
  }

  // Instalar PWA (trigger manual)
  public async installPWA(): Promise<boolean> {
    try {
      // Para iPhone, mostrar instruções nativas
      if (this.isIOS()) {
        this.showIOSInstallInstructions();
        return true;
      }

      // Para Android/Desktop com beforeinstallprompt
      const deferredPrompt = (window as any).deferredPrompt;
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('📱 Resultado da instalação:', outcome);
        return outcome === 'accepted';
      }

      return false;
    } catch (error) {
      console.error('❌ Erro ao instalar PWA:', error);
      return false;
    }
  }

  // Verificar se é iOS
  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  // Mostrar instruções para iOS
  private showIOSInstallInstructions(): void {
    const message = `
      Para instalar o app Gabriela CRM no seu iPhone:
      
      1. Toque no ícone de compartilhar (quadrado com seta)
      2. Role para baixo e toque em "Adicionar à Tela de Início"
      3. Toque em "Adicionar" no canto superior direito
      
      O app aparecerá na sua tela de início!
    `;
    
    alert(message);
  }

  // Enviar notificação local (fallback)
  public async sendLocalNotification(title: string, body: string, data?: any): Promise<void> {
    try {
      const permission = await this.requestNotificationPermission();
      
      if (permission !== 'granted') {
        console.warn('⚠️ Permissão negada para notificações');
        return;
      }

      const options: NotificationOptions = {
        body,
        icon: '/principal-logo.png',
        badge: '/principal-logo.png',
        vibrate: [200, 100, 200],
        data,
        tag: 'gabriela-notification',
        requireInteraction: true,
        actions: [
          {
            action: 'open',
            title: 'Abrir'
          },
          {
            action: 'close',
            title: 'Fechar'
          }
        ]
      };

      // Para iPhone, usar notificação do service worker
      if (this.registration) {
        await this.registration.showNotification(title, options);
      } else {
        // Fallback para notificação nativa
        new Notification(title, options);
      }

      console.log('✅ Notificação enviada:', title);
    } catch (error) {
      console.error('❌ Erro ao enviar notificação:', error);
    }
  }

  // Verificar se está em modo standalone (instalado)
  public isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || 
           (window.navigator as any).standalone === true;
  }

  // Detectar quando o app é instalado
  public onInstalled(callback: () => void): void {
    window.addEventListener('appinstalled', callback);
  }

  // Detectar quando pode ser instalado
  public onCanInstall(callback: (event: Event) => void): void {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      callback(e);
    });
  }

  // Limpar recursos
  public cleanup(): void {
    this.registration = null;
    this.subscription = null;
  }
}

// Export singleton instance
export const pwaNotificationService = PWANotificationService.getInstance();
